import { Component, ElementRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AlertMessageService } from 'src/app/services/alert-message/alert-message.service';
import { ProductImportationService } from 'src/app/services/product/importation/product-importation.service';
import { ProductImportHistoryDialogComponent } from './product-import-history-dialog/product-import-history-dialog.component';
import { MatDialog, MatDialogConfig, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { take, takeLast, takeUntil, takeWhile } from 'rxjs';
import { ProductImportation } from 'src/app/models/Product/Import/ProductImportation.model';
import { ColumnMap } from 'src/app/models/Product/Import/ColumnMap.model';
import { LoadingIndicatorComponent } from '../loading-indicator/loading-indicator.component';
import { FuncionalidadeSistemaId } from 'src/app/models/User/MinhaAcaoPermissao.model';
import { PermissaoAcessoService } from 'src/app/services/permissao/permissao-acesso.service';

@Component({
  selector: 'app-product-import-file-button',
  templateUrl: './product-import-file-button.component.html',
  styleUrls: ['./product-import-file-button.component.scss']
})
export class ProductImportFileButtonComponent implements OnInit {

  @ViewChild('input') inputFile!: ElementRef;

  @Input() isImportButtonEnable: boolean = true;
  @Input() buttonReplace: boolean = false;
  @Input() showHistoryButton: boolean = false;
  @Input() firstAccess: boolean = false;
  @Input() screenName: string = "";

  readonly funcionalidadeImportar = FuncionalidadeSistemaId.ImportarProdutos;

  private dialogRef: MatDialogRef<any> | undefined = undefined;
  private historyData: ProductImportation[] | null = null;
  private innerWidth: number = 0;

  constructor(
    private importService: ProductImportationService,
    private alertService: AlertMessageService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private router: Router,
    private permissaoAcesso: PermissaoAcessoService
  ) { }

  ngOnInit(): void {
    this.innerWidth = window.innerWidth;
    if (this.showHistoryButton == true) {
      this.importService.apiGetProductImportationPaginated().then(() => {
        this.importService.getImportationHistory().pipe(take(1)).forEach((importations) => {
          if (importations != null) {
            this.historyData = importations;
          }
        });
      });
    }
  }

  // Importar produtos tem funcionalidade própria (37), separada de Gravar: a permissão é verificada no clique, antes de abrir a janela de arquivos.
  // A autorização vale em todo /product, assim as telas de importação (/product/import...) não pedem a senha de novo.
  async abrirSeletorDeArquivo(event: Event, input: HTMLInputElement) {
    // O input fica dentro do botão: o clique dele sobe até aqui e não pode abrir a janela de novo
    if (event.target === input) return;

    if (await this.permissaoAcesso.verificarAcesso(this.funcionalidadeImportar, '/product')) {
      input.click();
    }
  }

  async importFile(event: any) {
    let updatingFile: boolean = false;
    let alreadyMapped: boolean = false;
    let alreadySent: boolean = false;
    let file: File = event.target.files[0];

    this.importService.cancelOnDestroy = this.firstAccess;

    if (!file) {
      this.alertService.alertErrorMessage(this.translate.instant("product.import.errors.unable-read-file"));
      this.inputFile.nativeElement.value = null;
      return;
    }

    let loadedFile = this.importService.getFile();
    updatingFile = loadedFile != null && loadedFile.name == file.name && this.buttonReplace == true;

    const dialogConfig = new MatDialogConfig();
    dialogConfig.hasBackdrop = true;
    dialogConfig.disableClose = true;
    dialogConfig.data = {message: this.translate.instant("product.import.reading-file")};
    let readingDialog = this.dialog.open(LoadingIndicatorComponent, dialogConfig);
    const data = await this.importService.readFile(file);
    if (data == null) {
      readingDialog.close();
      this.inputFile.nativeElement.value = null;
      return;
    }
    this.importService.mapColumns(data);

    this.importService.getColumnMap().pipe(take(2)).subscribe((map) => {
      if (map == null || Object.keys(map).length < 1) {
        return;
      }

      alreadyMapped = (map.id ?? 0 ) > 0 && map.headerRow == this.importService.removeHeaderEmptyColumnsAtEnd(data[0]);

      if (!alreadySent) {
        if ((updatingFile || alreadyMapped) && this.screenName != "column-mapping") {
          alreadySent = true;
          readingDialog.close();
          this.importService.sendFile().then(_ => {
            this.router.navigate(['/product'],{skipLocationChange:false}).then(()=>{
              this.router.navigate(['/product/import/review'], {skipLocationChange: true})
            });
          })
          return;
        }
        readingDialog.close();
        this.router.navigate(['/product'],{skipLocationChange:false}).then(()=>{
          this.router.navigate(['/product/import'], {skipLocationChange: true})
        });
      }
    });
  }

  @HostListener('window:resize')
  onResize() {
    this.innerWidth = window.innerWidth;
  }

  openDialog(event: any) {
    let targetAttr = event.target.getBoundingClientRect();

    if (this.dialogRef?.getState() == MatDialogState.OPEN) {
      this.dialogRef.close();
      return;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.hasBackdrop = true;
    dialogConfig.backdropClass = "invisible";
    dialogConfig.disableClose = false;
    dialogConfig.position = {
      top: (targetAttr.y + targetAttr.height + 8) + "px",
      right: (this.innerWidth - targetAttr.x - targetAttr.width - 23) + "px"
    }
    dialogConfig.data = this.historyData;

    this.dialogRef = this.dialog.open(ProductImportHistoryDialogComponent, dialogConfig);
  }
}